import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Bell, AlertTriangle, Zap, Check, X, BellRing } from "lucide-react";

const KIND = {
  posteggiante_lento: { label: "Posteggiante lento", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/15 border-amber-500/40" },
  richiesta_urgente: { label: "Richiesta urgente", icon: Zap, color: "text-red-400", bg: "bg-red-500/15 border-red-500/40" },
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function NotificationsBell() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin || user?.role === "admin";
  const [alerts, setAlerts] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const ctxRef = useRef(null);
  const loopRef = useRef(null);
  const known = useRef(null);

  const unacked = alerts.filter((a) => !a.acked_by_me);

  // Abilita l'audio dopo la prima interazione utente (limite browser autoplay)
  useEffect(() => {
    const enable = () => {
      try {
        if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (ctxRef.current.state === "suspended") ctxRef.current.resume();
        setAudioReady(true);
      } catch (e) { console.debug("Audio non disponibile", e); }
    };
    window.addEventListener("pointerdown", enable, { once: true });
    window.addEventListener("keydown", enable, { once: true });
    return () => { window.removeEventListener("pointerdown", enable); window.removeEventListener("keydown", enable); };
  }, []);

  const beep = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const play = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "square"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
    };
    play(880, 0, 0.18); play(1175, 0.22, 0.24);
  };

  const poll = async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data);
      const ids = new Set(data.filter((a) => !a.acked_by_me).map((a) => a.id));
      if (known.current === null) known.current = ids;
      else {
        const fresh = data.filter((a) => !a.acked_by_me && !known.current.has(a.id));
        fresh.forEach((a) => {
          const k = KIND[a.kind];
          toast.warning(`${k?.label || "Alert"}${a.articolo ? `: ${a.articolo}` : ""}`, {
            description: `${a.from_name || ""}${a.message ? " · " + a.message : ""}`,
            duration: 10000,
          });
        });
        known.current = ids;
      }
    } catch (e) { console.debug("Polling alert fallito", e); }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suono ripetuto finché ci sono alert non ancora presi in visione
  useEffect(() => {
    if (unacked.length > 0 && audioReady) {
      beep();
      loopRef.current = setInterval(beep, 4000);
    }
    return () => { if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unacked.length, audioReady]);

  const acknowledge = async (id) => {
    try {
      await api.put(`/alerts/${id}/acknowledge`);
      setAlerts((a) => a.map((x) => (x.id === id ? { ...x, acked_by_me: true } : x)));
      toast.success("Presa visione registrata");
    } catch { toast.error("Errore nella conferma"); }
  };

  const resolve = async (id) => {
    try { await api.put(`/alerts/${id}/resolve`); setAlerts((a) => a.filter((x) => x.id !== id)); }
    catch (e) { console.error("Risoluzione alert fallita", e); }
  };

  return (
    <>
      {/* Banner rosso lampeggiante finché non si conferma la presa visione */}
      {unacked.length > 0 && (
        <div data-testid="alert-banner" className="fixed top-0 inset-x-0 z-[80] bg-red-600 text-white animate-pulse shadow-lg" data-no-tr>
          <div className="max-w-[1400px] mx-auto flex items-center justify-center gap-3 px-4 py-2 text-sm font-semibold">
            <BellRing className="w-4 h-4 shrink-0" />
            <span>{unacked.length} alert da confermare</span>
            <button
              data-testid="alert-banner-open"
              onClick={() => setOpenList(true)}
              className="ml-1 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 text-xs font-bold transition-colors"
            >
              Vedi e conferma
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <button data-testid="notif-bell" onClick={() => setOpenList((o) => !o)}
          className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${unacked.length ? "bg-red-500/15 text-red-300 animate-pulse" : alerts.length ? "bg-amber-500/15 text-amber-300" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
          <Bell className="w-5 h-5" />
          {alerts.length > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{alerts.length}</span>
          )}
        </button>
        {openList && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpenList(false)} />
            <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50" data-no-tr>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <span className="font-display font-bold text-slate-100">Notifiche</span>
                <button onClick={() => setOpenList(false)} className="text-slate-400 hover:text-slate-100"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-2 space-y-2">
                {alerts.length === 0 && <p className="text-slate-500 text-sm p-3 text-center">Nessun alert attivo.</p>}
                {alerts.map((a) => {
                  const k = KIND[a.kind] || {};
                  const Icon = k.icon || Bell;
                  return (
                    <div key={a.id} data-testid={`notif-${a.id}`} className={`rounded-md border p-3 ${a.acked_by_me ? "border-slate-700 opacity-70" : k.bg || "border-slate-700"}`}>
                      <div className="flex items-start gap-2">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${k.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${k.color}`}>{k.label}{a.articolo ? `: ${a.articolo}` : ""}</div>
                          {a.message && <div className="text-xs text-slate-300 mt-0.5">{a.message}</div>}
                          <div className="text-[11px] text-slate-500 mt-1">{a.from_name}{a.posteggio ? ` · ${a.posteggio}` : ""} · {fmt(a.created_at)}</div>
                        </div>
                        {isAdmin && (
                          <button data-testid={`notif-resolve-${a.id}`} onClick={() => resolve(a.id)} title="Risolvi (rimuovi per tutti)" className="text-slate-400 hover:text-emerald-400"><Check className="w-4 h-4" /></button>
                        )}
                      </div>
                      {a.acked_by_me ? (
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-emerald-400 font-semibold"><Check className="w-3.5 h-3.5" /> Presa visione</div>
                      ) : (
                        <button
                          data-testid={`notif-ack-${a.id}`}
                          onClick={() => acknowledge(a.id)}
                          className="mt-2 w-full bg-red-500 hover:bg-red-400 text-white font-bold rounded-md py-1.5 text-sm flex items-center justify-center gap-1 transition-colors"
                        >
                          <Check className="w-4 h-4" /> OK, presa visione
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
